import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  Alert,
  AlertIcon,
  Box,
  Text,
  SimpleGrid,
  useToast
} from '@chakra-ui/react';
import { fighterService } from '../../services/fighterService';
import { imageService } from '../../services/imageService';
import { ImageUpload } from '../Common/ImageUpload';

// Weight classes for MMA
const WEIGHT_CLASSES = [
  'Strawweight',
  'Flyweight',
  'Bantamweight',
  'Featherweight',
  'Lightweight',
  'Welterweight',
  'Middleweight',
  'Light Heavyweight',
  'Heavyweight',
  'Super Heavyweight'
];

export const FighterEditForm = ({ fighter, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    weightClass: '',
    record: '',
    gym: '',
    hometown: '',
    age: '',
    height: '',
    reach: '',
    stance: '',
    bio: '',
    photoUrl: ''
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (fighter) {
      setFormData({
        name: fighter.name || '',
        nickname: fighter.nickname || '',
        weightClass: fighter.weightClass || '',
        record: fighter.record || '',
        gym: fighter.gym || '',
        hometown: fighter.hometown || '',
        age: fighter.age || '',
        height: fighter.height || '',
        reach: fighter.reach || '',
        stance: fighter.stance || '',
        bio: fighter.bio || '',
        photoUrl: fighter.photoUrl || ''
      });
    }
  }, [fighter]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handlePhotoUpload = async (file) => {
    try {
      setUploading(true);

      // Generate a unique path for the fighter photo
      const timestamp = Date.now();
      const fighterId = fighter?.id || 'new';
      const path = `fighters/${fighterId}/photo_${timestamp}`;

      // Upload the image
      const photoUrl = await imageService.uploadImage(file, path);

      // Update form data
      setFormData(prev => ({
        ...prev,
        photoUrl
      }));

      toast({
        title: 'Photo uploaded',
        description: 'Fighter photo has been uploaded successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.message || 'Failed to upload photo',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Fighter name is required');
      return false;
    }

    if (formData.age && (isNaN(formData.age) || formData.age < 18 || formData.age > 60)) {
      setError('Age must be a number between 18 and 60');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      const fighterData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null
      };

      if (fighter) {
        // Update existing fighter
        await fighterService.updateFighter(fighter.id, fighterData);
      } else {
        // Create new fighter
        await fighterService.createFighter(fighterData);
      }

      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save fighter');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={6} align="stretch">
        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {/* Photo Upload */}
        <FormControl>
          <FormLabel>Fighter Photo</FormLabel>
          <ImageUpload
            value={formData.photoUrl}
            onChange={setPhotoFile}
            onUploadComplete={handlePhotoUpload}
            placeholder="Upload fighter photo"
            aspectRatio="1/1"
            maxWidth="200px"
          />
          <Text fontSize="sm" color="gray.400" mt={2}>
            Recommended: Square image (400x400px or larger) in WebP, JPEG, or PNG format
          </Text>
        </FormControl>

        {/* Basic Information */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isRequired>
            <FormLabel>Name</FormLabel>
            <Input
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Fighter's full name"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Nickname</FormLabel>
            <Input
              value={formData.nickname}
              onChange={(e) => handleInputChange('nickname', e.target.value)}
              placeholder="Fighter's nickname (optional)"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Weight Class</FormLabel>
            <Select
              value={formData.weightClass}
              onChange={(e) => handleInputChange('weightClass', e.target.value)}
              placeholder="Select weight class"
            >
              {WEIGHT_CLASSES.map(weightClass => (
                <option key={weightClass} value={weightClass}>
                  {weightClass}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Record</FormLabel>
            <Input
              value={formData.record}
              onChange={(e) => handleInputChange('record', e.target.value)}
              placeholder="e.g., 15-3-0"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Gym/Team</FormLabel>
            <Input
              value={formData.gym}
              onChange={(e) => handleInputChange('gym', e.target.value)}
              placeholder="Fighter's gym or team"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Hometown</FormLabel>
            <Input
              value={formData.hometown}
              onChange={(e) => handleInputChange('hometown', e.target.value)}
              placeholder="Fighter's hometown"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Age</FormLabel>
            <Input
              type="number"
              min="18"
              max="60"
              value={formData.age}
              onChange={(e) => handleInputChange('age', e.target.value)}
              placeholder="Fighter's age"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Height</FormLabel>
            <Input
              value={formData.height}
              onChange={(e) => handleInputChange('height', e.target.value)}
              placeholder="e.g., 5'10&quot; or 178 cm"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Reach</FormLabel>
            <Input
              value={formData.reach}
              onChange={(e) => handleInputChange('reach', e.target.value)}
              placeholder="e.g., 72&quot; or 183 cm"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Stance</FormLabel>
            <Select
              value={formData.stance}
              onChange={(e) => handleInputChange('stance', e.target.value)}
              placeholder="Select stance"
            >
              <option value="Orthodox">Orthodox</option>
              <option value="Southpaw">Southpaw</option>
              <option value="Switch">Switch</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        {/* Bio */}
        <FormControl>
          <FormLabel>Biography</FormLabel>
          <Textarea
            value={formData.bio}
            onChange={(e) => handleInputChange('bio', e.target.value)}
            placeholder="Fighter's biography, achievements, fighting style, etc."
            rows={4}
          />
        </FormControl>

        {/* Action Buttons */}
        <HStack justify="flex-end" spacing={3}>
          <Button variant="ghost" onClick={onCancel} isDisabled={loading || uploading}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={loading || uploading}
            loadingText={uploading ? "Uploading..." : "Saving..."}
          >
            {fighter ? 'Update Fighter' : 'Create Fighter'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};
